import React, { useState, useRef } from 'react';
import ComponentExportButton from './ComponentExportButton';
import { Shield, Zap, Link2, AlertTriangle, CheckCircle2, AlertCircle, Info, ChevronDown, ChevronUp, Settings } from 'lucide-react';

interface ImplementationFactorsAnalysisProps {
  governanceScore: number;
  automationReadiness: number;
  integrationComplexity: number;
  regulatoryRisk: number;
  governanceAnalysis?: any;
}

const ImplementationFactorsAnalysis: React.FC<ImplementationFactorsAnalysisProps> = ({
  governanceScore,
  automationReadiness,
  integrationComplexity,
  regulatoryRisk,
  governanceAnalysis
}) => {
  const exportRef = useRef<HTMLDivElement>(null);
  const [expanded, setExpanded] = useState(false);

  const getScoreColor = (score: number) => {
    if (score <= 3) return 'text-green-600 bg-green-50 border-green-300';
    if (score <= 7) return 'text-yellow-600 bg-yellow-50 border-yellow-300';
    return 'text-red-600 bg-red-50 border-red-300';
  };

  const getScoreBadgeColor = (score: number) => {
    if (score <= 3) return 'bg-green-500';
    if (score <= 7) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  const getScoreLabel = (score: number) => {
    if (score <= 3) return 'Low';
    if (score <= 7) return 'Medium';
    return 'High';
  };

  const getScoreIcon = (score: number) => {
    if (score <= 3) return <CheckCircle2 className="w-4 h-4" />;
    if (score <= 7) return <AlertCircle className="w-4 h-4" />;
    return <AlertTriangle className="w-4 h-4" />;
  };

  const getEaseOfImplementation = () => {
    const avgComplexity = (governanceScore + integrationComplexity + regulatoryRisk) / 3;

    if (avgComplexity <= 3) {
      return {
        level: 'Very Easy',
        color: 'bg-green-500',
        borderColor: 'border-green-400',
        bgColor: 'bg-green-50',
        textColor: 'text-green-900',
        icon: <CheckCircle2 className="w-5 h-5 text-green-600" />,
        description: 'This automation can be implemented quickly with minimal approvals. Low governance barriers and simple integration requirements make this an ideal quick-win opportunity.',
        timeframe: '1-3 weeks',
        approvalLevel: 'Team/Department level'
      };
    } else if (avgComplexity <= 6) {
      return {
        level: 'Moderate',
        color: 'bg-yellow-500',
        borderColor: 'border-yellow-400',
        bgColor: 'bg-yellow-50',
        textColor: 'text-yellow-900',
        icon: <AlertCircle className="w-5 h-5 text-yellow-600" />,
        description: 'Moderate complexity requiring some cross-functional coordination and approvals. Plan for a phased approach starting with manual trigger to prove ROI before pursuing full automation.',
        timeframe: '4-8 weeks',
        approvalLevel: 'Department Head + IT'
      };
    } else {
      return {
        level: 'Complex',
        color: 'bg-red-500',
        borderColor: 'border-red-400',
        bgColor: 'bg-red-50',
        textColor: 'text-red-900',
        icon: <AlertTriangle className="w-5 h-5 text-red-600" />,
        description: 'High complexity with significant governance requirements. Strongly recommend starting with Phase 1 (manual trigger) to demonstrate value and build stakeholder support before pursuing enterprise-level approvals.',
        timeframe: '2-4 months',
        approvalLevel: 'Executive + Security + Architecture'
      };
    }
  };

  const implementationEase = getEaseOfImplementation();

  const ScoreTooltip: React.FC<{ children: React.ReactNode; content: string }> = ({ children, content }) => {
    const [show, setShow] = useState(false);
    return (
      <div className="relative inline-block">
        <div
          onMouseEnter={() => setShow(true)}
          onMouseLeave={() => setShow(false)}
        >
          {children}
        </div>
        {show && (
          <div className="absolute z-50 bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg shadow-lg w-64">
            {content}
            <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1">
              <div className="border-4 border-transparent border-t-gray-900"></div>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div ref={exportRef} className="bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 border-2 border-purple-200 rounded-xl overflow-hidden shadow-lg">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full p-4 flex items-center justify-between hover:bg-purple-100/50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Settings className="w-5 h-5 text-purple-600" />
          <h3 className="text-lg font-bold text-gray-900">Implementation Factors Analysis</h3>
        </div>
        {expanded ? (
          <ChevronUp className="w-5 h-5 text-purple-600" />
        ) : (
          <ChevronDown className="w-5 h-5 text-purple-600" />
        )}
      </button>

      <div className="px-4 pb-4">
        <div className="flex justify-end mb-2">
          <ComponentExportButton targetRef={exportRef} filename="implementation-factors" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div className={`${implementationEase.bgColor} border-2 ${implementationEase.borderColor} rounded-lg p-4`}>
            <div className="flex items-center justify-between mb-3">
              <h4 className={`text-sm font-bold ${implementationEase.textColor} flex items-center gap-2`}>
                {implementationEase.icon}
                Ease of Implementation
              </h4>
              <span className={`${implementationEase.color} text-white px-3 py-1 rounded-full text-xs font-bold`}>
                {implementationEase.level}
              </span>
            </div>
            <p className={`text-xs ${implementationEase.textColor} leading-relaxed mb-3`}>
              {implementationEase.description}
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-white/60 rounded p-2">
                <p className="text-xs opacity-75 mb-1">Expected Timeline</p>
                <p className="text-sm font-bold">{implementationEase.timeframe}</p>
              </div>
              <div className="bg-white/60 rounded p-2">
                <p className="text-xs opacity-75 mb-1">Approval Level</p>
                <p className="text-sm font-bold">{implementationEase.approvalLevel}</p>
              </div>
            </div>
          </div>

          <div className="bg-amber-50 border-2 border-amber-300 rounded-lg p-4">
            <h4 className="text-sm font-bold text-amber-900 mb-3 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" />
              Key Challenges
            </h4>
            {governanceAnalysis?.key_challenges && governanceAnalysis.key_challenges.length > 0 ? (
              <div className="space-y-2">
                {governanceAnalysis.key_challenges.map((challenge: any, idx: number) => (
                  <div key={idx} className="bg-white rounded p-2 border border-amber-200">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`px-2 py-0.5 rounded text-xs font-bold ${
                        challenge.severity === 'High' ? 'bg-red-100 text-red-700' :
                        challenge.severity === 'Medium' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-green-100 text-green-700'
                      }`}>
                        {challenge.severity}
                      </span>
                      <span className="text-xs font-semibold text-gray-800">{challenge.area}</span>
                    </div>
                    <p className="text-xs text-gray-600 mb-1">{challenge.description}</p>
                    <p className="text-xs text-blue-600">💡 {challenge.mitigation}</p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-white rounded p-3 border border-green-200">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-600" />
                  <span className="text-sm text-green-700 font-medium">No significant challenges identified</span>
                </div>
                <p className="text-xs text-gray-600 mt-1">This opportunity has low complexity and can proceed with minimal barriers.</p>
              </div>
            )}
          </div>
        </div>

        {expanded && (
          <div className="space-y-4 pt-4 border-t border-purple-200">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className={`p-4 rounded-lg border-2 ${getScoreColor(governanceScore)}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Shield className="w-5 h-5" />
                    <span className="font-semibold text-sm">Governance Complexity</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <ScoreTooltip content="Higher score = more approvals, integrations, and governance barriers needed. Lower score = easier to implement.">
                      <Info className="w-4 h-4 text-gray-500 cursor-help" />
                    </ScoreTooltip>
                    {getScoreIcon(governanceScore)}
                    <span className="font-bold text-2xl">{governanceScore}/10</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 mb-2">
                  <div className={`w-3 h-3 rounded-full ${getScoreBadgeColor(governanceScore)}`}></div>
                  <span className="text-sm font-semibold">{getScoreLabel(governanceScore)}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full transition-all ${getScoreBadgeColor(governanceScore)}`}
                    style={{ width: `${governanceScore * 10}%` }}
                  ></div>
                </div>
                {governanceAnalysis?.score_explanations?.governance_complexity && (
                  <div className="mt-2 px-2 py-1.5 bg-white/50 rounded text-xs font-medium">
                    💡 {governanceAnalysis.score_explanations.governance_complexity}
                  </div>
                )}
              </div>

              <div className={`p-4 rounded-lg border-2 ${getScoreColor(11 - automationReadiness)}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Zap className="w-5 h-5" />
                    <span className="font-semibold text-sm">Automation Readiness</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <ScoreTooltip content="Higher score = process is ready for automation with minimal barriers. Lower score = requires more preparation before automation.">
                      <Info className="w-4 h-4 text-gray-500 cursor-help" />
                    </ScoreTooltip>
                    {getScoreIcon(11 - automationReadiness)}
                    <span className="font-bold text-2xl">{automationReadiness}/10</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 mb-2">
                  <div className={`w-3 h-3 rounded-full ${automationReadiness >= 8 ? 'bg-green-500' : automationReadiness >= 5 ? 'bg-yellow-500' : 'bg-red-500'}`}></div>
                  <span className="text-sm font-semibold">
                    {automationReadiness >= 8 ? 'High' : automationReadiness >= 5 ? 'Medium' : 'Low'}
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full transition-all ${automationReadiness >= 8 ? 'bg-green-500' : automationReadiness >= 5 ? 'bg-yellow-500' : 'bg-red-500'}`}
                    style={{ width: `${automationReadiness * 10}%` }}
                  ></div>
                </div>
                {governanceAnalysis?.score_explanations?.automation_readiness && (
                  <div className="mt-2 px-2 py-1.5 bg-white/50 rounded text-xs font-medium">
                    💡 {governanceAnalysis.score_explanations.automation_readiness}
                  </div>
                )}
              </div>

              <div className={`p-4 rounded-lg border-2 ${getScoreColor(integrationComplexity)}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Link2 className="w-5 h-5" />
                    <span className="font-semibold text-sm">Integration Complexity</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <ScoreTooltip content="Number of systems that need to be connected. Higher score = more integrations required. Lower score = simpler or no integrations.">
                      <Info className="w-4 h-4 text-gray-500 cursor-help" />
                    </ScoreTooltip>
                    {getScoreIcon(integrationComplexity)}
                    <span className="font-bold text-2xl">{integrationComplexity}/10</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 mb-2">
                  <div className={`w-3 h-3 rounded-full ${getScoreBadgeColor(integrationComplexity)}`}></div>
                  <span className="text-sm font-semibold">{getScoreLabel(integrationComplexity)}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full transition-all ${getScoreBadgeColor(integrationComplexity)}`}
                    style={{ width: `${integrationComplexity * 10}%` }}
                  ></div>
                </div>
                {governanceAnalysis?.score_explanations?.integration_complexity && (
                  <div className="mt-2 px-2 py-1.5 bg-white/50 rounded text-xs font-medium">
                    💡 {governanceAnalysis.score_explanations.integration_complexity}
                  </div>
                )}
              </div>

              <div className={`p-4 rounded-lg border-2 ${getScoreColor(regulatoryRisk)}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5" />
                    <span className="font-semibold text-sm">Regulatory Risk</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <ScoreTooltip content="Level of regulatory/compliance risk. Higher score = involves sensitive data, financial transactions, or compliance requirements. Lower score = standard business process.">
                      <Info className="w-4 h-4 text-gray-500 cursor-help" />
                    </ScoreTooltip>
                    {getScoreIcon(regulatoryRisk)}
                    <span className="font-bold text-2xl">{regulatoryRisk}/10</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 mb-2">
                  <div className={`w-3 h-3 rounded-full ${getScoreBadgeColor(regulatoryRisk)}`}></div>
                  <span className="text-sm font-semibold">{getScoreLabel(regulatoryRisk)}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full transition-all ${getScoreBadgeColor(regulatoryRisk)}`}
                    style={{ width: `${regulatoryRisk * 10}%` }}
                  ></div>
                </div>
                {governanceAnalysis?.score_explanations?.regulatory_risk && (
                  <div className="mt-2 px-2 py-1.5 bg-white/50 rounded text-xs font-medium">
                    💡 {governanceAnalysis.score_explanations.regulatory_risk}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ImplementationFactorsAnalysis;
